import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  FileSearchOutlined,
  InboxOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd'
import {
  confirmFileBatch,
  getFileBatch,
  getFileBatchExecution,
  listFileBatches,
  prepareFileBatchPreview,
  uploadFileBatch,
  validateFileBatch,
} from '@/api/etl'
import type {
  FileBatch,
  FileIntakeExecutionStatus,
  FileIntakeExecutionSubmission,
  IntakeEvent,
  RegistryAcceptance,
  UploadedIntakeFile,
} from '@/types/etl'
import { useImpersonationStore } from '@/store/impersonation'

const { Dragger } = Upload
const { Text, Title } = Typography

const registryAcceptanceOptions = [
  { value: 'intermediate', label: 'Промежуточный' },
  { value: 'final', label: 'Итоговый' },
] satisfies Array<{ value: RegistryAcceptance; label: string }>

const statusColors: Record<string, string> = {
  uploaded: 'default',
  validated: 'green',
  invalid: 'red',
  preview_ready: 'gold',
  confirmed: 'blue',
}

const executionStatusColors: Record<string, string> = {
  queued: 'gold',
  leased: 'processing',
  running: 'processing',
  succeeded: 'success',
  blocked: 'warning',
  failed: 'error',
  rolled_back: 'warning',
  rollback_failed: 'error',
  cleanup_failed: 'error',
}

const terminalExecutionStatuses = new Set([
  'succeeded',
  'blocked',
  'failed',
  'rolled_back',
  'rollback_failed',
  'cleanup_failed',
])

const stepLabels: Record<string, string> = {
  privacy_clean: 'Очистить ПДн',
  backup_candidate: 'Создать backup candidate',
  replace_registry_period: 'Заменить период реестров',
  replace_fines_period: 'Заменить период снятий',
  replace_structure_period: 'Заменить структуру',
  replace_bed_fund_period: 'Заменить коечный фонд',
  rebuild_derived_once: 'Один раз пересобрать производные данные',
  verify_candidate_reference: 'Сравнить candidate и reference',
}

const categoryLabels: Record<string, string> = {
  registry_zip: 'Реестр ZIP',
  fines_zip: 'МЭК ZIP',
  source_bundle_zip: 'Исходный архив',
  structure_excel: 'Структура',
  bed_fund_excel: 'Коечный фонд',
  excel_report: 'Excel',
  dbf_file: 'DBF',
  pdf_report: 'PDF',
  unknown_zip: 'ZIP',
  other: 'Другое',
}

const formatBytes = (value: number) => {
  if (value < 1024) return `${value} Б`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} КБ`
  return `${(value / 1024 / 1024).toFixed(1)} МБ`
}

type ApiErrorResponse = {
  status?: number
  data?: { detail?: string }
}

type BatchIntent = {
  generation: number
  orgId: string | null
  batchId: string | null
}

const apiErrorResponse = (error: unknown): ApiErrorResponse | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) return undefined
  return (error as { response?: ApiErrorResponse }).response
}

const eventItems = (events: IntakeEvent[]) => (
  <ul style={{ margin: 0, paddingLeft: 18 }}>
    {events.slice(0, 8).map((event, index) => (
      <li key={`${event.code}-${index}`}>
        <Text type={event.severity === 'error' ? 'danger' : undefined}>
          {event.location ? `${event.location}: ` : ''}
          {event.message}
        </Text>
      </li>
    ))}
    {events.length > 8 && (
      <li>
        <Text type="secondary">...и ещё {events.length - 8}</Text>
      </li>
    )}
  </ul>
)

export default function FileIntake() {
  const { impersonatedOrgId, impersonatedOrgName } = useImpersonationStore()
  const [form] = Form.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [batch, setBatch] = useState<FileBatch | null>(null)
  const [execution, setExecution] = useState<
    FileIntakeExecutionSubmission | FileIntakeExecutionStatus | null
  >(null)
  const [executionPolling, setExecutionPolling] = useState(false)
  const [recent, setRecent] = useState<FileBatch[]>([])
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(false)
  const [preparing, setPreparing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [defaultRegistryAcceptance, setDefaultRegistryAcceptance] =
    useState<RegistryAcceptance>('intermediate')
  const [registryAcceptanceOverrides, setRegistryAcceptanceOverrides] = useState<
    Record<string, RegistryAcceptance>
  >({})
  const batchIntentRef = useRef<BatchIntent>({ generation: 0, orgId: null, batchId: null })
  const recentRequestRef = useRef(0)
  const pollAbortRef = useRef<AbortController | null>(null)

  const beginBatchIntent = useCallback((orgId: string | null, batchId: string | null) => {
    pollAbortRef.current?.abort()
    pollAbortRef.current = null
    const intent = {
      generation: batchIntentRef.current.generation + 1,
      orgId,
      batchId,
    }
    batchIntentRef.current = intent
    return intent
  }, [])

  const isCurrentBatchIntent = useCallback((intent: BatchIntent) => {
    const current = batchIntentRef.current
    return (
      current.generation === intent.generation &&
      current.orgId === intent.orgId &&
      current.batchId === intent.batchId &&
      useImpersonationStore.getState().impersonatedOrgId === intent.orgId
    )
  }, [])

  const isCurrentIntentGeneration = useCallback((intent: BatchIntent) => {
    const current = batchIntentRef.current
    return (
      current.generation === intent.generation &&
      current.orgId === intent.orgId &&
      useImpersonationStore.getState().impersonatedOrgId === intent.orgId
    )
  }, [])

  const bindBatchToIntent = useCallback(
    (intent: BatchIntent, batchId: string) => {
      if (!isCurrentBatchIntent(intent)) return null
      const boundIntent = { ...intent, batchId }
      batchIntentRef.current = boundIntent
      return boundIntent
    },
    [isCurrentBatchIntent]
  )

  const refreshRecent = useCallback(async () => {
    const requestId = recentRequestRef.current + 1
    recentRequestRef.current = requestId
    const requestOrgId = impersonatedOrgId
    if (!requestOrgId) {
      setRecent([])
      return
    }
    try {
      const result = await listFileBatches({ limit: 10 })
      if (
        recentRequestRef.current === requestId &&
        useImpersonationStore.getState().impersonatedOrgId === requestOrgId
      ) {
        setRecent(result.items)
      }
    } catch {
      // Recent list is secondary; upload errors are handled explicitly.
    }
  }, [impersonatedOrgId])

  useEffect(() => {
    beginBatchIntent(impersonatedOrgId, null)
    setBatch(null)
    setExecution(null)
    setExecutionPolling(false)
    setRecent([])
    setFileList([])
    setLoading(false)
    setValidating(false)
    setPreparing(false)
    setConfirming(false)
    setConfirmOpen(false)
    setDefaultRegistryAcceptance('intermediate')
    setRegistryAcceptanceOverrides({})
    void refreshRecent()
  }, [beginBatchIntent, impersonatedOrgId, refreshRecent])

  useEffect(() => {
    if (
      !batch ||
      !executionPolling ||
      !impersonatedOrgId ||
      (execution && terminalExecutionStatuses.has(execution.status))
    ) {
      return
    }
    const intent = batchIntentRef.current
    if (
      intent.orgId !== impersonatedOrgId ||
      intent.batchId !== batch.id ||
      !isCurrentBatchIntent(intent)
    ) {
      return
    }

    let cancelled = false
    let timeoutId: number | undefined
    let activeController: AbortController | null = null
    const retryDelay = execution?.status === 'queued' ? 3000 : 2000
    const schedulePoll = (delay: number) => {
      timeoutId = window.setTimeout(
        async () => {
          if (cancelled || !isCurrentBatchIntent(intent)) return
          const controller = new AbortController()
          activeController = controller
          pollAbortRef.current = controller
          try {
            const next = await getFileBatchExecution(batch.id, controller.signal)
            if (
              !cancelled &&
              isCurrentBatchIntent(intent) &&
              next.file_batch_id === batch.id
            ) {
              setExecution(next)
              setExecutionPolling(!terminalExecutionStatuses.has(next.status))
            }
          } catch (error: unknown) {
            if (cancelled || !isCurrentBatchIntent(intent)) return
            const status = apiErrorResponse(error)?.status
            if (status === 404 || status === 503) {
              setExecution(null)
              setExecutionPolling(false)
              return
            }
            schedulePoll(retryDelay)
          } finally {
            if (pollAbortRef.current === controller) pollAbortRef.current = null
            if (activeController === controller) activeController = null
          }
        },
        delay
      )
    }

    schedulePoll(execution ? retryDelay : 0)
    return () => {
      cancelled = true
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
      activeController?.abort()
      if (pollAbortRef.current === activeController) pollAbortRef.current = null
    }
  }, [batch, execution, executionPolling, impersonatedOrgId, isCurrentBatchIntent])

  const selectedFiles = useMemo(() => {
    return fileList
      .map((item) => item.originFileObj as File | undefined)
      .filter((file): file is File => Boolean(file))
  }, [fileList])

  const preparePreviewForBatch = async (
    targetBatch: FileBatch,
    intent: BatchIntent,
    defaultAcceptance: RegistryAcceptance,
    acceptanceOverrides: Record<string, RegistryAcceptance>
  ): Promise<FileBatch | null> => {
    if (targetBatch.status !== 'validated' && targetBatch.status !== 'preview_ready') {
      return null
    }
    if (!intent.orgId || intent.batchId !== targetBatch.id) return null
    setPreparing(true)
    try {
      const prepared = await prepareFileBatchPreview(
        targetBatch.id,
        {
          default_registry_acceptance: defaultAcceptance,
          registry_acceptance_overrides: acceptanceOverrides,
        },
        intent.orgId
      )
      if (!isCurrentBatchIntent(intent) || prepared.id !== targetBatch.id) return null
      setBatch(prepared)
      return isCurrentBatchIntent(intent) ? prepared : null
    } catch (error: unknown) {
      if (isCurrentBatchIntent(intent)) {
        message.warning(
          apiErrorResponse(error)?.data?.detail ||
            'Пачка сохранена, но единый план пока не подготовлен'
        )
      }
      return null
    } finally {
      if (isCurrentBatchIntent(intent)) setPreparing(false)
    }
  }

  const handleUpload = async () => {
    const requestOrgId = impersonatedOrgId
    if (!requestOrgId) {
      message.error('Сначала выберите ЛПУ в переключателе рабочего контекста')
      return
    }
    const values = await form.validateFields()
    if (selectedFiles.length === 0) {
      message.error('Добавьте файлы пачки')
      return
    }
    const intent = beginBatchIntent(requestOrgId, null)
    setRegistryAcceptanceOverrides({})

    setLoading(true)
    try {
      const result = await uploadFileBatch({
        name: values.name,
        files: selectedFiles,
        expectedOrgId: requestOrgId,
      })
      const boundIntent = bindBatchToIntent(intent, result.id)
      if (!boundIntent) {
        if (useImpersonationStore.getState().impersonatedOrgId !== requestOrgId) {
          message.info('Контекст ЛПУ изменился; результат не показан')
        } else {
          await refreshRecent()
        }
        return
      }
      if (!isCurrentBatchIntent(boundIntent)) {
        message.info('Контекст ЛПУ изменился; результат не показан')
        await refreshRecent()
        return
      }
      setBatch(result)
      setExecution(null)
      setExecutionPolling(false)
      setFileList([])
      if (result.status === 'invalid') {
        message.warning('Пачка загружена, есть ошибки валидации')
      } else {
        const prepared = await preparePreviewForBatch(
          result,
          boundIntent,
          defaultRegistryAcceptance,
          {}
        )
        if (prepared) {
          message.success('Пачка проверена; единый план подготовлен')
        }
      }
      await refreshRecent()
    } catch (error: unknown) {
      if (isCurrentBatchIntent(intent)) {
        message.error(apiErrorResponse(error)?.data?.detail || 'Ошибка загрузки пачки')
      }
    } finally {
      if (isCurrentIntentGeneration(intent)) setLoading(false)
    }
  }

  const handleValidate = async () => {
    if (!batch || !impersonatedOrgId) return
    const requestOrgId = impersonatedOrgId
    const requestBatchId = batch.id
    const intent = beginBatchIntent(requestOrgId, requestBatchId)
    setExecution(null)
    setExecutionPolling(false)
    setValidating(true)
    try {
      const result = await validateFileBatch(requestBatchId, requestOrgId)
      if (!isCurrentBatchIntent(intent) || result.id !== requestBatchId) return
      setBatch(result)
      if (result.status === 'validated') {
        const prepared = await preparePreviewForBatch(
          result,
          intent,
          defaultRegistryAcceptance,
          registryAcceptanceOverrides
        )
        if (prepared) message.success('Manifest и единый план пересчитаны')
      } else {
        message.warning('Manifest пересчитан, но содержит ошибки')
      }
      await refreshRecent()
    } catch (error: unknown) {
      if (isCurrentBatchIntent(intent)) {
        message.error(apiErrorResponse(error)?.data?.detail || 'Ошибка проверки пачки')
      }
    } finally {
      if (isCurrentIntentGeneration(intent)) setValidating(false)
    }
  }

  const handlePrepare = async () => {
    if (!batch || !impersonatedOrgId) return
    const intent = beginBatchIntent(impersonatedOrgId, batch.id)
    setExecution(null)
    setExecutionPolling(false)
    const prepared = await preparePreviewForBatch(
      batch,
      intent,
      defaultRegistryAcceptance,
      registryAcceptanceOverrides
    )
    if (prepared) message.success('Единый план подготовлен')
    await refreshRecent()
  }

  const handleConfirm = async () => {
    if (!batch || !impersonatedOrgId || !batch.confirmation_digest) return
    const requestOrgId = impersonatedOrgId
    const requestBatchId = batch.id
    const confirmationDigest = batch.confirmation_digest
    const intent = beginBatchIntent(requestOrgId, requestBatchId)
    setConfirming(true)
    try {
      const result = await confirmFileBatch(
        requestBatchId,
        {
          confirmation_digest: confirmationDigest,
        },
        requestOrgId
      )
      if (!isCurrentBatchIntent(intent) || result.batch.id !== requestBatchId) return
      setBatch(result.batch)
      setExecution(result.execution || null)
      setExecutionPolling(
        result.batch.status === 'confirmed' &&
          (!result.execution ||
            !terminalExecutionStatuses.has(result.execution.status))
      )
      setConfirmOpen(false)
      await refreshRecent()
      if (!isCurrentBatchIntent(intent)) return
      message.success(
        result.execution
          ? 'План подтверждён и поставлен в очередь'
          : 'План подтверждён; постановка выполнения отключена'
      )
    } catch (error: unknown) {
      if (!isCurrentBatchIntent(intent)) return
      if (apiErrorResponse(error)?.status === 409) {
        setConfirmOpen(false)
        try {
          const refreshed = await getFileBatch(requestBatchId)
          if (isCurrentBatchIntent(intent) && refreshed.id === requestBatchId) {
            setBatch(refreshed)
            setExecution(null)
            setExecutionPolling(refreshed.status === 'confirmed')
          }
        } catch {
          // The primary 409 is more useful than a secondary refresh error.
        }
        if (isCurrentBatchIntent(intent)) {
          message.warning('План изменился или устарел; состояние пачки обновлено')
        }
      } else {
        message.error(apiErrorResponse(error)?.data?.detail || 'Ошибка подтверждения пачки')
      }
    } finally {
      if (isCurrentIntentGeneration(intent)) setConfirming(false)
    }
  }

  const actionBusy = loading || validating || preparing || confirming

  const uploadProps: UploadProps = {
    multiple: true,
    accept: '.zip,.xlsx,.xlsm,.dbf,.pdf',
    fileList,
    disabled: actionBusy,
    beforeUpload: () => false,
    onChange: ({ fileList: nextList }) => setFileList(nextList),
  }

  const handleSelectBatch = async (batchId: string) => {
    const requestOrgId = impersonatedOrgId
    if (!requestOrgId) return
    const intent = beginBatchIntent(requestOrgId, batchId)
    setBatch(null)
    setExecution(null)
    setExecutionPolling(false)
    setConfirmOpen(false)
    setLoading(false)
    setValidating(false)
    setPreparing(false)
    setConfirming(false)
    try {
      const result = await getFileBatch(batchId)
      if (isCurrentBatchIntent(intent) && result.id === batchId) {
        setBatch(result)
        setExecutionPolling(result.status === 'confirmed')
        const registrySteps =
          result.unified_preview?.operation_plan.steps.filter(
            (step) => step.kind === 'replace_registry_period'
          ) || []
        const firstAcceptance = registrySteps[0]?.registry_acceptance as
          | RegistryAcceptance
          | undefined
        if (firstAcceptance) {
          setDefaultRegistryAcceptance(firstAcceptance)
          const nextOverrides: Record<string, RegistryAcceptance> = {}
          registrySteps.forEach((step) => {
            const acceptance = step.registry_acceptance as RegistryAcceptance | undefined
            if (acceptance && acceptance !== firstAcceptance) {
              step.periods.forEach((period) => {
                nextOverrides[period] = acceptance
              })
            }
          })
          setRegistryAcceptanceOverrides(nextOverrides)
        } else {
          setDefaultRegistryAcceptance('intermediate')
          setRegistryAcceptanceOverrides({})
        }
      }
    } catch (error: unknown) {
      if (isCurrentBatchIntent(intent)) {
        message.error(apiErrorResponse(error)?.data?.detail || 'Ошибка получения пачки')
      }
    }
  }

  const files = batch?.files || []
  const summary = batch?.summary || {}
  const preview = batch?.unified_preview
  const plan = preview?.operation_plan
  const registryPeriods = summary.observed_periods || []
  const previewRegistryAcceptance = new Map<string, RegistryAcceptance>()
  plan?.steps
    .filter((step) => step.kind === 'replace_registry_period')
    .forEach((step) => {
      if (step.registry_acceptance) {
        step.periods.forEach((period) => {
          previewRegistryAcceptance.set(period, step.registry_acceptance as RegistryAcceptance)
        })
      }
    })
  const previewMatchesAcceptance = registryPeriods.every(
    (period) =>
      previewRegistryAcceptance.get(period) ===
      (registryAcceptanceOverrides[period] || defaultRegistryAcceptance)
  )
  const canConfirm = Boolean(
    batch?.status === 'preview_ready' &&
      (batch.errors?.length || 0) === 0 &&
      preview?.ready_for_confirmation &&
      batch.confirmation_digest === preview.confirmation_digest &&
      previewMatchesAcceptance &&
      !actionBusy
  )
  const workflowStep = !batch
    ? 0
    : batch.status === 'confirmed'
      ? 5
      : batch.status === 'preview_ready'
        ? 4
        : batch.status === 'validated'
          ? 1
          : 0

  const fileColumns = [
    {
      title: 'ID файла',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
      width: 150,
      render: (value: string) => (
        <Text code copyable={{ text: value }}>
          {value.slice(0, 8)}
        </Text>
      ),
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      width: 140,
      render: (category: string) => (
        <Tag>{categoryLabels[category] || category}</Tag>
      ),
    },
    {
      title: 'Период',
      dataIndex: 'period_yyyy_mm',
      key: 'period_yyyy_mm',
      width: 110,
      render: (period: string | null, row: UploadedIntakeFile) => (
        period || row.period_mmyy || '-'
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'registry_type',
      key: 'registry_type',
      width: 70,
      render: (value: string | null) => value || '-',
    },
    {
      title: 'Размер',
      dataIndex: 'size_bytes',
      key: 'size_bytes',
      width: 110,
      align: 'right' as const,
      render: (value: number) => formatBytes(value),
    },
    {
      title: 'SHA256',
      dataIndex: 'sha256',
      key: 'sha256',
      width: 150,
      render: (value: string) => <Text code>{value.slice(0, 12)}</Text>,
    },
    {
      title: 'События',
      dataIndex: 'validation_messages',
      key: 'validation_messages',
      width: 220,
      render: (events: IntakeEvent[]) => {
        if (!events || events.length === 0) return <Text type="secondary">-</Text>
        return (
          <Space size={4} wrap>
            {events.map((event, index) => (
              <Tag
                key={`${event.code}-${index}`}
                color={event.severity === 'error' ? 'red' : 'gold'}
              >
                {event.code}
              </Tag>
            ))}
          </Space>
        )
      },
    },
  ]

  const recentColumns = [
    {
      title: 'Создана',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (value: string) => new Date(value).toLocaleString('ru-RU'),
    },
    {
      title: 'ЛПУ',
      dataIndex: 'lpu_code',
      key: 'lpu_code',
      width: 90,
    },
    {
      title: 'Пачка',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (value: string | null) => value || '-',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Файлы',
      dataIndex: ['summary', 'total_files'],
      key: 'total_files',
      width: 80,
      align: 'right' as const,
      render: (value: number | undefined) => value || 0,
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Alert
        type={impersonatedOrgId ? 'info' : 'warning'}
        showIcon
        message={
          impersonatedOrgId
            ? `Рабочий контекст: ${impersonatedOrgName || impersonatedOrgId}`
            : 'ЛПУ не выбрано'
        }
        description={
          impersonatedOrgId
            ? 'Код и целевая stage-база определяются сервером из этого контекста.'
            : 'Выберите Лобню или Щёлково в переключателе ЛПУ в шапке.'
        }
      />

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Название пачки" name="name">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Статус загружаемых реестров"
              tooltip="Применяется ко всем периодам; после загрузки можно задать исключение для отдельного периода"
            >
              <Select<RegistryAcceptance>
                value={defaultRegistryAcceptance}
                options={registryAcceptanceOptions}
                disabled={actionBusy}
                onChange={(value) => {
                  setDefaultRegistryAcceptance(value)
                  setRegistryAcceptanceOverrides({})
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Нажмите или перетащите ZIP/XLSX/DBF/PDF файлы
          </p>
          <p className="ant-upload-hint">
            Upload сохраняет original files и строит dry-run manifest; ETL не запускается
          </p>
        </Dragger>
      </Form>

      <Space wrap>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          loading={loading}
          onClick={handleUpload}
          disabled={
            actionBusy || !impersonatedOrgId || selectedFiles.length === 0
          }
        >
          Загрузить пачку
        </Button>
        <Button
          icon={<FileSearchOutlined />}
          loading={validating}
          onClick={handleValidate}
          disabled={actionBusy || !batch || batch.status === 'confirmed'}
        >
          Проверить manifest
        </Button>
        <Button
          icon={<SafetyCertificateOutlined />}
          loading={preparing}
          onClick={handlePrepare}
          disabled={
            actionBusy ||
            !batch ||
            !['validated', 'preview_ready'].includes(batch.status) ||
            (batch.errors?.length || 0) > 0
          }
        >
          {preview ? 'Пересобрать план' : 'Подготовить план'}
        </Button>
        <Button
          icon={<SafetyCertificateOutlined />}
          loading={confirming}
          disabled={!canConfirm}
          onClick={() => setConfirmOpen(true)}
        >
          Подтвердить план
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => void refreshRecent()}>
          Обновить список
        </Button>
      </Space>

      {batch && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Steps
            current={workflowStep}
            size="small"
            items={[
              { title: 'Файлы' },
              { title: 'Очистка ПДн' },
              { title: 'Проверки' },
              { title: 'План' },
              { title: 'Подтверждение' },
              { title: 'Результат' },
            ]}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <Title level={4} style={{ margin: 0 }}>
              Пачка {batch.lpu_code}
            </Title>
            <Tag color={statusColors[batch.status] || 'default'}>{batch.status}</Tag>
          </div>

          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Файлов" value={summary.total_files || 0} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="ZIP реестров" value={summary.registry_zip_count || 0} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Периодов" value={summary.observed_period_count || 0} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Ошибок"
                  value={batch.errors?.length || 0}
                  valueStyle={{ color: (batch.errors?.length || 0) > 0 ? '#cf1322' : '#3f8600' }}
                />
              </Card>
            </Col>
          </Row>

          {batch.errors && batch.errors.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="Ошибки валидации"
              description={eventItems(batch.errors)}
            />
          )}

          {batch.warnings && batch.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Предупреждения"
              description={eventItems(batch.warnings)}
            />
          )}

          {registryPeriods.length > 1 && batch.status !== 'confirmed' && (
            <Card size="small" title="Статус реестров по периодам">
              <Space wrap>
                {registryPeriods.map((period) => (
                  <Space key={period}>
                    <Text code>{period}</Text>
                    <Select<RegistryAcceptance>
                      style={{ width: 170 }}
                      value={
                        registryAcceptanceOverrides[period] || defaultRegistryAcceptance
                      }
                      options={registryAcceptanceOptions}
                      disabled={actionBusy}
                      onChange={(value) => {
                        setRegistryAcceptanceOverrides((current) => {
                          const next = { ...current }
                          if (value === defaultRegistryAcceptance) delete next[period]
                          else next[period] = value
                          return next
                        })
                      }}
                    />
                  </Space>
                ))}
              </Space>
            </Card>
          )}

          {preview && !previewMatchesAcceptance && batch.status !== 'confirmed' && (
            <Alert
              type="warning"
              showIcon
              message="Статус реестра изменён после подготовки плана"
              description="Пересоберите план: подтверждение старого digest заблокировано интерфейсом и серверной привязкой."
            />
          )}

          {batch.status === 'validated' && !preview && (
            <Alert
              type="info"
              showIcon
              message="Файлы проверены; единый preview ещё не подготовлен"
              description="Подтверждение станет доступно только после серверных preflight и dry-run очистки ПДн. Состояние БД и политика очистки не принимаются из браузера."
            />
          )}

          {batch.status === 'confirmed' && (
            <Alert
              type={
                execution?.status === 'succeeded'
                  ? 'success'
                  : execution && terminalExecutionStatuses.has(execution.status)
                    ? 'warning'
                    : 'info'
              }
              showIcon
              message={
                execution
                  ? `Выполнение: ${execution.status}`
                  : executionPolling
                    ? 'Проверяем статус выполнения'
                    : 'Подтверждение зафиксировано; постановка выполнения отключена'
              }
              description={
                execution ? (
                  <Space wrap>
                    <Tag color={executionStatusColors[execution.status] || 'default'}>
                      {execution.status}
                    </Tag>
                    <Text code copyable={{ text: execution.execution_id }}>
                      {execution.execution_id.slice(0, 8)}
                    </Text>
                    {'attempt_count' in execution && (
                      <Text type="secondary">Попыток: {execution.attempt_count}</Text>
                    )}
                    {'failure_code' in execution && execution.failure_code && (
                      <Text type="danger">{execution.failure_code}</Text>
                    )}
                  </Space>
                ) : executionPolling ? (
                  'Статус ещё не получен; временные ошибки будут повторены автоматически.'
                ) : (
                  'Receipt сохранён без job; повторных действий оператора не требуется до включения серверного gate.'
                )
              }
            />
          )}

          {preview && plan && (
            <Card size="small" title="Единый план операции">
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Сценарий">
                  <Tag color="blue">{plan.scenario}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Candidate">
                  <Text code>{plan.candidate_database}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Reference (только чтение)">
                  <Text code>{plan.reference_database}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Снимок исходных копий">
                  <Text code>{plan.source_snapshot_id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Политика ПДн">
                  <Space wrap>
                    <Text>{preview.privacy.policy_id}</Text>
                    <Tag>{preview.privacy.policy_version}</Tag>
                    <Text type="secondary">
                      {preview.privacy.files.length} чувствительных файлов
                    </Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Проверки">
                  <Space wrap>
                    {preview.checks.map((check) => (
                      <Tag
                        key={`${check.code}-${check.detail}`}
                        color={
                          check.status === 'pass'
                            ? 'green'
                            : check.status === 'warning'
                              ? 'orange'
                              : 'red'
                        }
                      >
                        {check.code}: {check.status}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Digest плана">
                  <Text code copyable>
                    {plan.operation_digest}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Manifest">
              {batch.manifest_sha256 ? <Text code>{batch.manifest_sha256}</Text> : '-'}
            </Descriptions.Item>
            {batch.confirmed_at && (
              <Descriptions.Item label="Confirmed">
                <Space>
                  <CheckCircleOutlined />
                  {new Date(batch.confirmed_at).toLocaleString('ru-RU')}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Table
            columns={fileColumns}
            dataSource={files}
            rowKey="id"
            size="small"
            scroll={{ x: 1200 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100'],
            }}
          />
        </Space>
      )}

      <div>
        <Title level={4}>Последние пачки</Title>
        <Table
          columns={recentColumns}
          dataSource={recent}
          rowKey="id"
          size="small"
          pagination={false}
          onRow={(row) => ({
            onClick: () => void handleSelectBatch(row.id),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      <Modal
        title="Подтверждение пачки"
        open={confirmOpen}
        okText="Подтвердить"
        cancelText="Отмена"
        okButtonProps={{ loading: confirming, disabled: !canConfirm }}
        onOk={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Подтверждается ровно показанный серверный план."
            description="Цели, этапы, force-флаги и callback нельзя изменить из браузера. При включённом серверном контуре тот же шаг атомарно поставит операцию в очередь; inline ETL в HTTP-запросе не выполняется."
          />
          {plan?.steps.map((step) => (
            <div key={`${step.ordinal}-${step.kind}`}>
              <Space align="start">
                <Tag color={step.mutates_candidate ? 'orange' : 'default'}>
                  {step.ordinal}
                </Tag>
                <div>
                  <Text strong>{stepLabels[step.kind] || step.kind}</Text>
                  {step.periods.length > 0 && (
                    <div>
                      <Text type="secondary">Периоды: {step.periods.join(', ')}</Text>
                    </div>
                  )}
                </div>
              </Space>
            </div>
          ))}
          {batch?.confirmation_digest && (
            <Text type="secondary" copyable>
              Confirmation digest: {batch.confirmation_digest}
            </Text>
          )}
        </Space>
      </Modal>
    </Space>
  )
}
